import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Users, UserCheck, Percent, History, Map as MapIcon, MapPin,
  Download, FileSpreadsheet, FileText, Loader2,
} from 'lucide-react'
import writeXlsxFile from 'write-excel-file/browser'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useToast } from '@/shared/hooks/use-toast'
import {
  useConfiguracionFidelizacion,
  useMetadataFidelizacion,
  useTodosParticipantesFidelizacion,
} from '../hooks/useFidelizacion'
import { usePeriodoReporte } from '../hooks/usePeriodoReporte'
import { kpiGradientClasses, kpiIconClasses, normalizeColor } from '../lib/reporteColor'
import { CAMPO_REPRESENTANTE_CANDIDATES, pickCampo, pickCampoFiltrable } from '../lib/campos'
import { nombreDistritoReporte } from '../lib/distritosMedifarmaPeru'
import { ReporteHeader } from '../components/ReporteHeader'
import { ReporteSubNav } from '../components/ReporteSubNav'
import { MonthFilter } from '../components/MonthFilter'
import { BarChartAgrupado, type DatasetOpcion } from '../components/charts/BarChartAgrupado'

// created_at llega como ISO UTC. Convertir al timezone del programa antes de
// comparar para evitar que registros de las últimas horas del día caigan en el día siguiente.
const TZ_PERU = 'America/Lima'
function toLocalDate(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso.slice(0, 10)
  return new Intl.DateTimeFormat('en-CA', { timeZone: TZ_PERU, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d)
}

/** True si la fecha ISO `created_at` cae dentro del rango [desde, hasta] (inclusive). */
function enRango(createdAt: unknown, desde: string, hasta: string): boolean {
  if (typeof createdAt !== 'string' || createdAt.length < 10) return false
  const dia = toLocalDate(createdAt)
  return dia >= desde && dia <= hasta
}

/**
 * Agrupa participantes por una etiqueta y devuelve, por grupo, dos conteos:
 * `value` = total de registrados y `value2` = cuántos cumplen `esActivo`
 * (participantes con puntos). Se ordena por el total (registrados).
 */
function agruparConteoDual(
  items: Array<Record<string, unknown>>,
  getLabel: (p: Record<string, unknown>) => unknown,
  esActivo: (p: Record<string, unknown>) => boolean,
): Array<{ label: string; value: number; value2: number }> {
  const m = new Map<string, { total: number; activos: number }>()
  for (const p of items) {
    const raw = getLabel(p)
    if (raw == null || String(raw).trim() === '') continue
    const label = String(raw)
    const cur = m.get(label) ?? { total: 0, activos: 0 }
    cur.total += 1
    if (esActivo(p)) cur.activos += 1
    m.set(label, cur)
  }
  return Array.from(m.entries())
    .map(([label, v]) => ({ label, value: v.total, value2: v.activos }))
    .sort((a, b) => b.value - a.value)
}

const extra = (p: Record<string, unknown>) =>
  (p.datos_extra as Record<string, unknown> | undefined) ?? {}
const puntosDe = (p: Record<string, unknown>) =>
  parseFloat(String(p.puntos_disponibles ?? '0')) || 0
/** True si el participante tiene puntos (> 0) = "participante activo". */
const tienePuntos = (p: Record<string, unknown>): boolean => puntosDe(p) > 0

/** Filtro "Puntos": 'todos' | 'con' (>0) | 'sin' (≤0). Client-side. */
type FiltroPuntos = 'todos' | 'con' | 'sin'
/** True si el participante tiene el email verificado (= validado en el id 7). */
const esVerificado = (p: Record<string, unknown>): boolean => {
  const e = extra(p)
  const v = e.email_verificado ?? p.email_verificado
  return v === true || v === 'true' || v === 1 || v === '1'
}

/** Una fila del export: un participante del periodo. */
interface FilaParticipante {
  nombre: string
  representante: string
  departamento: string
  distrito: string
  puntos: number
  activo: 'Sí' | 'No'
}

const EXPORT_HEADERS = ['Nombre', 'Representante', 'Departamento', 'Distrito', 'Puntos', 'Activo']

/** Escapa un valor para CSV (comillas si contiene coma, comilla o salto de línea). */
function csvCell(val: string | number): string {
  const s = String(val)
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

/** Dispara la descarga de un Blob en el navegador. */
function descargarArchivo(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/**
 * Tab Participantes para Medifarma Perú (id 7): participantes activos por área.
 *
 * Todo se calcula client-side desde el listado completo de participantes
 * (`useTodosParticipantesFidelizacion`, que ya pagina internamente). Un
 * participante se considera "activo" cuando acumuló > 0 puntos.
 */
export function ParticipantesActivosPage() {
  const { id } = useParams<{ id: string }>()
  const configId = id ? parseInt(id, 10) : 0

  const config = useConfiguracionFidelizacion(configId)
  const metadata = useMetadataFidelizacion(configId)
  const todosParticipantes = useTodosParticipantesFidelizacion(configId)

  const {
    mes,
    setMes,
    sinFecha,
    setSinFecha,
    modo,
    setModo,
    rangoPersonalizado,
    setRangoPersonalizado,
    rango,
  } = usePeriodoReporte({ sinFechaInicial: true })

  const color = normalizeColor(config.data)
  const gradient = kpiGradientClasses(color.nombre)
  const c = kpiIconClasses(color.nombre, color.tono)

  // Resolución de campos según metadata (el upstream nombra los campos distinto
  // por cliente). Representante vive en `datos_extra`; distrito en `datos_extra`
  // como id (`ciudad`) que se resuelve con el catálogo local; departamento es la
  // provincia de primer nivel del participante.
  const camposExternos = metadata.data?.campos_externos
  const campoRep = useMemo(
    () => pickCampo(camposExternos, CAMPO_REPRESENTANTE_CANDIDATES),
    [camposExternos],
  )
  const campoRepFiltro = useMemo(
    () => pickCampoFiltrable(camposExternos, CAMPO_REPRESENTANTE_CANDIDATES),
    [camposExternos],
  )
  const repKey = campoRepFiltro?.key ?? campoRep?.key ?? null
  const campoCiudad = useMemo(() => pickCampo(camposExternos, ['ciudad', 'distrito']), [camposExternos])
  const ciudadKey = campoCiudad?.key ?? null

  // Filtro "Puntos" (client-side): con puntos (>0) / sin puntos (≤0). Afecta
  // KPIs, gráficas por área y export.
  const [filtroPuntos, setFiltroPuntos] = useState<FiltroPuntos>('todos')

  // Participantes del periodo seleccionado (por fecha de registro).
  const enPeriodoBase = useMemo(() => {
    const data = todosParticipantes.data ?? []
    if (sinFecha) return data
    return data.filter((p) => enRango(p.created_at, rango.desde, rango.hasta))
  }, [todosParticipantes.data, sinFecha, rango])

  // Conjunto base tras aplicar el filtro por puntos.
  const enPeriodo = useMemo(() => {
    if (filtroPuntos === 'todos') return enPeriodoBase
    return enPeriodoBase.filter((p) =>
      filtroPuntos === 'con' ? puntosDe(p) > 0 : puntosDe(p) <= 0,
    )
  }, [enPeriodoBase, filtroPuntos])

  const activos = useMemo(() => enPeriodo.filter((p) => puntosDe(p) > 0), [enPeriodo])

  // KPIs.
  const registrados = enPeriodo.length
  const conPuntos = activos.length
  const validadosCount = useMemo(() => enPeriodo.filter(esVerificado).length, [enPeriodo])
  const pendientesCount = registrados - validadosCount
  const pctParticipacion = validadosCount > 0 ? (conPuntos / validadosCount) * 100 : 0

  // Agrupaciones por área: cada grupo muestra registrados (todos del periodo) y
  // cuántos tienen puntos (> 0). Se agrupa sobre `enPeriodo`, no sobre `activos`.
  const repData = useMemo(
    () =>
      repKey
        ? agruparConteoDual(
            enPeriodo,
            (p) => extra(p)[`${repKey}_nombre`] ?? extra(p)[repKey],
            tienePuntos,
          )
        : [],
    [enPeriodo, repKey],
  )
  const departamentoData = useMemo(
    () => agruparConteoDual(enPeriodo, (p) => p.provincia_nombre ?? p.provincia, tienePuntos),
    [enPeriodo],
  )
  const distritoData = useMemo(
    () =>
      ciudadKey
        ? agruparConteoDual(
            enPeriodo,
            (p) => {
              const val = extra(p)[ciudadKey]
              return val != null ? nombreDistritoReporte(configId, String(val)) ?? String(val) : null
            },
            tienePuntos,
          )
        : [],
    [enPeriodo, ciudadKey, configId],
  )

  const agrupacionOptions = useMemo<DatasetOpcion[]>(
    () => [
      {
        key: 'representante',
        label: campoRep?.label ?? 'Representante',
        icon: UserCheck,
        data: repData,
        isLoading: todosParticipantes.isLoading,
        error: todosParticipantes.error,
        headerStat: { value: repData.length, label: 'únicos' },
      },
      {
        key: 'departamento',
        label: 'Departamento',
        icon: MapIcon,
        data: departamentoData,
        isLoading: todosParticipantes.isLoading,
        error: todosParticipantes.error,
        headerStat: { value: departamentoData.length, label: 'únicos' },
      },
      {
        key: 'distrito',
        label: 'Distrito',
        icon: MapPin,
        data: distritoData,
        isLoading: todosParticipantes.isLoading,
        error: todosParticipantes.error,
        headerStat: { value: distritoData.length, label: 'únicos' },
      },
    ],
    [campoRep, repData, departamentoData, distritoData, todosParticipantes.isLoading, todosParticipantes.error],
  )

  // ---- Export (client-side): una fila por participante del periodo ----
  const { toast } = useToast()
  const [exportando, setExportando] = useState<'xlsx' | 'csv' | null>(null)

  const filasExport = useMemo<FilaParticipante[]>(
    () =>
      enPeriodo.map((p) => {
        const puntos = puntosDe(p)
        const repRaw = repKey ? extra(p)[`${repKey}_nombre`] ?? extra(p)[repKey] : ''
        const ciudadRaw = ciudadKey ? extra(p)[ciudadKey] : undefined
        const distrito =
          ciudadRaw != null && String(ciudadRaw).trim() !== ''
            ? nombreDistritoReporte(configId, String(ciudadRaw)) ?? String(ciudadRaw)
            : ''
        return {
          nombre: `${String(p.nombre ?? '')} ${String(p.apellido ?? '')}`.trim(),
          representante: String(repRaw ?? ''),
          departamento: String(p.provincia_nombre ?? p.provincia ?? ''),
          distrito,
          puntos,
          activo: puntos > 0 ? 'Sí' : 'No',
        }
      }),
    [enPeriodo, repKey, ciudadKey, configId],
  )

  const exportar = async (formato: 'xlsx' | 'csv') => {
    if (filasExport.length === 0) {
      toast({
        title: 'Sin datos',
        description: 'No hay participantes para exportar en el periodo seleccionado.',
        variant: 'destructive',
      })
      return
    }
    setExportando(formato)
    try {
      if (formato === 'csv') {
        const lineas = [
          EXPORT_HEADERS.join(','),
          ...filasExport.map((f) =>
            [f.nombre, f.representante, f.departamento, f.distrito, f.puntos, f.activo]
              .map(csvCell)
              .join(','),
          ),
        ]
        // BOM utf-8 para que Excel respete los acentos.
        const blob = new Blob(['﻿' + lineas.join('\r\n')], {
          type: 'text/csv;charset=utf-8',
        })
        descargarArchivo(blob, 'participantes.csv')
      } else {
        await writeXlsxFile(filasExport, {
          columns: [
            { header: 'Nombre', width: 30, cell: (f) => f.nombre },
            { header: 'Representante', width: 24, cell: (f) => f.representante },
            { header: 'Departamento', width: 20, cell: (f) => f.departamento },
            { header: 'Distrito', width: 20, cell: (f) => f.distrito },
            { header: 'Puntos', width: 12, cell: (f) => ({ type: Number, value: f.puntos }) },
            { header: 'Activo', width: 10, cell: (f) => f.activo },
          ],
        }).toFile('participantes.xlsx')
      }
      toast({ title: 'Descarga lista', description: `participantes.${formato}` })
    } catch {
      toast({
        title: 'Error al exportar',
        description: 'No se pudo generar el archivo.',
        variant: 'destructive',
      })
    } finally {
      setExportando(null)
    }
  }

  const handleRefresh = () => {
    config.refetch()
    metadata.refetch()
    todosParticipantes.refetch()
  }
  const refreshing = config.isFetching || metadata.isFetching || todosParticipantes.isFetching

  return (
    <div className="p-6 space-y-6">
      <ReporteHeader
        config={config.data}
        seccion="Participantes"
        icon={Users}
        onRefresh={handleRefresh}
        refreshing={refreshing}
      />
      <ReporteSubNav configId={configId} colorNombre={color.nombre} colorTono={color.tono} />

      {/* Filtro de periodo (por fecha de registro): Todo el histórico / Mes / Rango. */}
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Periodo</Label>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant={sinFecha ? 'default' : 'outline'}
                className={
                  sinFecha
                    ? `bg-${color.nombre}-700 hover:bg-${color.nombre}-800 text-white`
                    : 'border-gray-300 dark:border-gray-700'
                }
                onClick={() => setSinFecha(!sinFecha)}
              >
                <History className="h-4 w-4" />
                Todo el histórico
              </Button>
              <div className={`flex items-center gap-2 ${sinFecha ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="flex items-center rounded-md border border-gray-300 dark:border-gray-700 p-0.5">
                  {(['mes', 'rango'] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setModo(m)}
                      className={`rounded px-2 py-0.5 text-xs font-medium transition-colors ${
                        modo === m
                          ? `bg-${color.nombre}-700 text-white`
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {m === 'mes' ? 'Mes' : 'Rango'}
                    </button>
                  ))}
                </div>
                {modo === 'rango' ? (
                  <div className="flex items-center gap-1.5">
                    <Input
                      type="date"
                      value={rangoPersonalizado.desde || ''}
                      min={metadata.data?.rango_datos?.primera_factura ?? undefined}
                      max={rangoPersonalizado.hasta || metadata.data?.rango_datos?.ultima_factura || undefined}
                      onChange={(e) =>
                        setRangoPersonalizado({ ...rangoPersonalizado, desde: e.target.value || undefined })
                      }
                      className="h-9 w-[9.5rem]"
                      aria-label="Desde"
                    />
                    <span className="text-xs text-muted-foreground">→</span>
                    <Input
                      type="date"
                      value={rangoPersonalizado.hasta || ''}
                      min={rangoPersonalizado.desde || metadata.data?.rango_datos?.primera_factura || undefined}
                      max={metadata.data?.rango_datos?.ultima_factura ?? undefined}
                      onChange={(e) =>
                        setRangoPersonalizado({ ...rangoPersonalizado, hasta: e.target.value || undefined })
                      }
                      className="h-9 w-[9.5rem]"
                      aria-label="Hasta"
                    />
                  </div>
                ) : (
                  <MonthFilter
                    value={mes}
                    onChange={setMes}
                    minDate={metadata.data?.rango_datos?.primera_factura ?? null}
                    maxDate={metadata.data?.rango_datos?.ultima_factura ?? null}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Filtro "Activos": activo = tiene puntos (>0); inactivo = ≤0.
              Afecta KPIs, gráficas por área y export. */}
          <div className="space-y-1.5">
            <Label className="text-xs">Actividad</Label>
            <div className="flex items-center rounded-md border border-gray-300 dark:border-gray-700 p-0.5">
              {(['todos', 'con', 'sin'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setFiltroPuntos(v)}
                  className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                    filtroPuntos === v
                      ? `bg-${color.nombre}-700 text-white`
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {v === 'todos' ? 'Todos' : v === 'con' ? 'Activos' : 'Inactivos'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Exportar: listado de participantes del periodo (xlsx / csv). */}
        <div className="flex items-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                className={`bg-${color.nombre}-700 hover:bg-${color.nombre}-800 text-white`}
                disabled={exportando !== null || todosParticipantes.isLoading || filasExport.length === 0}
              >
                {exportando ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Exportar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel>Participantes</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault()
                  void exportar('xlsx')
                }}
              >
                <FileSpreadsheet className="h-4 w-4" />
                Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault()
                  void exportar('csv')
                }}
              >
                <FileText className="h-4 w-4" />
                CSV (.csv)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Participantes con puntos</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {todosParticipantes.isLoading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <>
                <div className="text-2xl font-bold text-black dark:text-white">
                  {conPuntos.toLocaleString('es-PE')}
                </div>
                <p className="text-xs text-muted-foreground mt-1">participantes activos</p>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Participantes registrados</CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {todosParticipantes.isLoading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <>
                <div className="text-2xl font-bold text-black dark:text-white">
                  {registrados.toLocaleString('es-PE')}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {validadosCount.toLocaleString('es-PE')} validados · {pendientesCount.toLocaleString('es-PE')} pendientes
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card className={`border border-gray-300 dark:border-gray-700 ${gradient}`}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">% de participación</CardTitle>
            <Percent className={`h-4 w-4 ${c.icon}`} />
          </CardHeader>
          <CardContent>
            {todosParticipantes.isLoading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <>
                <div className="text-2xl font-bold text-black dark:text-white">
                  {pctParticipacion.toLocaleString('es-PE', {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })}
                  %
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  con puntos sobre validados
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Por área: registrados (todos) vs. con puntos (> 0), por
          representante / departamento / distrito. */}
      <BarChartAgrupado
        title="Participantes por área"
        colorNombre={color.nombre}
        icon={UserCheck}
        options={agrupacionOptions}
        primaryLabel="Registrados"
        secondaryLabel="Con puntos"
        configId={configId}
        secondaryPctOfPrimary
      />
    </div>
  )
}
